import { AutocompleteRenderOptionState, SxProps } from '@mui/material'
import { FieldProps, FormikValues } from 'formik'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ObjectSchema } from 'yup'
import { VinciFormModal } from '~/components'
import { VinciAutocomplete } from '~/components/VinciAutocomplete'
import { useFetch } from '~/hooks/useFetch'
import useRequestWithToast from '~/hooks/useRequestWithToast'
import { parseSubObjectIds } from '~/utils/parseSubObjectIds'
import removeArrayDuplicates from '~/utils/removeArrayDuplicates'
import { TModalStates } from '~/utils/types'

interface ILocalAutocompleteProps<T> extends FieldProps {
  label: string
  primaryIdentifier: string
  optionLabelGetter?: (item: T) => string
  error?: boolean
  helperText?: string | false | undefined
  disabled?: boolean
  sx: SxProps
  size?: 'medium' | 'small'
}

interface IModalProps<T> {
  form: {
    initialValues: T
    validationSchema?: ObjectSchema<any>
  }
  title?: string
  children: ReactNode
}

interface IResourceRequestParams {
  [index: string]: any
}

interface IResourceAutocompleteProps<T> {
  /**
   * Resource location url at API.
   */
  resourcePath: string
  /**
   *  Params to be used in resource request.
   */
  requestParams?: IResourceRequestParams
  /**
   * Function to sort resources.
   */
  resourceSortComparator?: (a: T, b: T) => number
  /**
   * Function to filter resources listed.
   * @param item Current item to apply the filter.
   */
  resourceFilter?: (item: T) => boolean
  /**
   * Function to get resource by response.
   * @param response
   */
  resourceGetter?: (response: any) => T[]
  /**
   * Function that fires when a item is selected
   * @param item Item selected in the autocomplete list.
   */
  onSelectResource?: (item: T) => void | Promise<void>
  /**
   * Autocomplete props to be repassed.
   */
  autocompleteProps: ILocalAutocompleteProps<T>
  /**
   * VinciFormModal props to be repassed
   */
  modalProps?: IModalProps<T>
  /**
   * Request (useRequestWithToast) props to be repassed.
   */
  requestProps: {
    initialText: string
    errorText: string
    successText: string
    /**
     * Create resource function to be used as callback.
     */
    createRequestFunction?: (props: any) => Promise<any>
    /**
     * SuccessCallback reference from `useRequestWithToast`
     */
    successCallback?: (response: any) => void
  }
  parseResourceBeforeCreate?: boolean
  customValue?: string
  /**
   * Should logout if resource request returns unauthorized.
   */
  logoutOnUnauthorized?: boolean
  /**
   * Function to try select a value based on input value when it's blurred.
   * @param value Value in autocomplete when blur event it's fired.
   * @param options Resource options to select.
   */
  matchValueOnBlur?: (value: string, options: T[]) => T | undefined
  additionalOptions?: T[]
  shouldFilterAdditionalOptions?: boolean
  onInputChange?: (newValue: string) => void | Promise<void>
  uniqueKey?: string
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: any,
    state: AutocompleteRenderOptionState,
  ) => React.ReactNode
  onCreate?: (initialString: string) => void
}

export const useResourceAutocomplete = () => {
  const { t } = useTranslation()
  function ResourceAutocomplete<T>({
    resourcePath,
    resourceGetter,
    requestParams,
    autocompleteProps,
    modalProps,
    requestProps,
    resourceSortComparator,
    resourceFilter,
    onSelectResource,
    parseResourceBeforeCreate = true,
    logoutOnUnauthorized = false,
    matchValueOnBlur,
    additionalOptions = [],
    shouldFilterAdditionalOptions = true,
    onInputChange,
    uniqueKey = '_id',
    renderOption,
    onCreate,
    customValue = '',
  }: IResourceAutocompleteProps<T>) {
    const parsedRequestParams = useMemo(
      () =>
        new URLSearchParams({
          ...requestParams,
        }).toString(),
      [requestParams],
    )

    const {
      data: rawData = [],
      loading,
      mutate,
    } = useFetch<T[]>(`${resourcePath}?${parsedRequestParams}`, true, {
      focusThrottleInterval: 50000,
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: true,
      revalidateIfStale: false,
      logoutOnUnauthorized: logoutOnUnauthorized,
    })
    const [resourceModal, setResourceModal] = useState<TModalStates | null>(null)
    const [createResourceValue, setCreateResourceValue] = useState<string>('')

    const resourceData = useMemo(() => {
      if (resourceGetter) {
        return resourceGetter(rawData)
      }

      return rawData as T[]
    }, [resourceGetter, rawData])

    const data = useMemo(() => {
      const mergedData = [...resourceData]
      if (shouldFilterAdditionalOptions) mergedData.push(...additionalOptions)

      if (!resourceFilter && !resourceSortComparator) return mergedData

      const filteredResource: T[] = resourceFilter
        ? (mergedData || []).filter(resourceFilter)
        : mergedData

      const sortedResource: T[] = resourceSortComparator
        ? filteredResource.sort(resourceSortComparator)
        : filteredResource

      if (!shouldFilterAdditionalOptions) sortedResource.push(...additionalOptions)

      return removeArrayDuplicates(sortedResource as Record<string, unknown>[], uniqueKey)
    }, [
      resourceData,
      resourceFilter,
      resourceSortComparator,
      additionalOptions,
      shouldFilterAdditionalOptions,
      uniqueKey,
    ])

    const { request } = useRequestWithToast()

    const handleCreateResource = useCallback((name: string) => {
      setCreateResourceValue(name)
      setResourceModal('create')
    }, [])

    const handleSelectResource = async (resource: T) => {
      autocompleteProps.form.setFieldValue(autocompleteProps.field.name, resource)

      if (onSelectResource) onSelectResource(resource)

      await handleCloseResourceModal()
    }

    const validValue = useMemo(
      () => data.includes(autocompleteProps.field.value),
      [autocompleteProps.field.value, data],
    )
    const defaultRequestFunction = async () => {
      throw new Error('Request function not provided')
    }

    const handleSaveResource = async (values: FormikValues) => {
      const resource = parseResourceBeforeCreate ? parseSubObjectIds(values) : values

      const response = await request<T>({
        ...requestProps,
        requestProps: resource as unknown as T,
        requestFunction: requestProps.createRequestFunction || defaultRequestFunction,
        successCallback: requestProps.successCallback,
      })

      if (response) {
        await handleSelectResource(response as T)
      }
    }

    const handleCloseResourceModal = async () => {
      setResourceModal(null)
      await mutate()
    }

    const modalValues: FormikValues = useMemo(() => {
      if (!modalProps) {
        return {
          [autocompleteProps.primaryIdentifier]: createResourceValue,
        }
      }
      return {
        ...modalProps.form.initialValues,
        [autocompleteProps.primaryIdentifier]: createResourceValue,
      }
    }, [modalProps, autocompleteProps, createResourceValue])

    const handleCreate = useCallback(
      (value: string): void => {
        if (onCreate) {
          return onCreate(value)
        }

        if (requestProps.createRequestFunction) {
          return handleCreateResource(value)
        }
      },
      [onCreate, requestProps.createRequestFunction, handleCreateResource],
    )

    return (
      <>
        <VinciAutocomplete
          {...autocompleteProps}
          label={
            loading ? `${autocompleteProps.label} ${t('(Loading...)')}` : autocompleteProps.label
          }
          disabled={loading || autocompleteProps.disabled}
          options={data}
          onCreate={handleCreate}
          onSelect={handleSelectResource}
          handleBlur={async (value: string) => {
            if (matchValueOnBlur && !validValue) {
              const shouldSelect = matchValueOnBlur(value, data)

              shouldSelect && (await handleSelectResource(shouldSelect))
            }
          }}
          onInputChange={(_, value) => onInputChange?.(value)}
          renderOption={renderOption}
          uniqueKey={uniqueKey}
          customValue={customValue}
        />
        {modalProps && (
          <VinciFormModal
            form={{
              initialValues: modalValues,
              onSubmit: handleSaveResource,
              validationSchema: modalProps.form.validationSchema,
            }}
            onClose={handleCloseResourceModal}
            opened={resourceModal !== null}
            title={modalProps.title ?? 'Resource Modal'}
            alwaysPromptOnClose
          >
            {modalProps.children}
          </VinciFormModal>
        )}
      </>
    )
  }

  return ResourceAutocomplete
}
